import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { brandColors, brandFonts } from '../../styled/brand'
import { Wrap } from './Wrap'

/**
 * Shared layout for the legal pages (Privacy Policy, EULA). A cream hero band
 * with a "Legal" kicker + H1, then a narrow ~720px reading column. Body copy is
 * written as semantic markup inside <LegalSection> blocks, which style their own
 * headings, paragraphs, lists, and links to the brand.
 */

const LegalTitle = styled('h1')(({ theme }) => ({
  margin: '20px 0 0',
  fontFamily: brandFonts.heading,
  fontWeight: 700,
  fontSize: 52,
  lineHeight: 1.08,
  letterSpacing: '-0.02em',
  color: brandColors.nearBlack2,
  [theme.breakpoints.down('md')]: {
    fontSize: 40,
  },
}))

/**
 * A block of legal copy. Styles its descendant elements so page authors can
 * write plain <h3>/<p>/<ul>/<strong>/<a> inside it.
 */
export const LegalSection = styled(Box)({
  marginTop: 44,
  '&:first-of-type': { marginTop: 0 },
  '& h3': {
    margin: '0 0 14px',
    fontFamily: brandFonts.heading,
    fontWeight: 700,
    fontSize: 22,
    letterSpacing: '-0.01em',
    color: brandColors.nearBlack2,
  },
  '& p': {
    margin: '0 0 16px',
    fontSize: 17,
    lineHeight: 1.75,
    color: brandColors.legalBody,
    fontWeight: 400,
  },
  '& p:last-child': { marginBottom: 0 },
  '& ul, & ol': { margin: '0 0 16px', paddingLeft: 22 },
  '& li': {
    fontSize: 17,
    lineHeight: 1.75,
    color: brandColors.legalBody,
    marginBottom: 6,
  },
  '& strong': { fontWeight: 700 },
  '& a': { color: brandColors.coralText },
})

/** A cream-tinted callout card (e.g. the EULA "Note"). */
export const LegalCallout = styled(LegalSection)({
  background: brandColors.creamLight,
  borderRadius: 14,
  padding: '24px 28px',
})

const LegalPage = ({
  title,
  meta,
  children,
}: {
  title: React.ReactNode
  meta?: React.ReactNode
  children: React.ReactNode
}) => (
  <Box sx={{ background: brandColors.cream }}>
    <Box sx={{ padding: '40px 0 100px' }}>
      <Wrap>
        <LegalTitle className="h1">{title}</LegalTitle>
        {meta}
        <Box sx={{ maxWidth: 720, mx: 'auto', mt: '56px' }}>{children}</Box>
      </Wrap>
    </Box>
  </Box>
)

export default LegalPage
